import { useTranslation } from "react-i18next";
import { AlertTriangle, Award, Factory, FlaskConical, Pill, Tag } from "lucide-react";
import type { Product } from "@nh/shared";
import { ProductBlockHeading } from "./ProductBlockHeading";

export type ProductFactsData = NonNullable<Product["facts"]>;

export function hasFacts(facts: ProductFactsData | undefined): boolean {
  if (!facts) return false;
  return Boolean(
    facts.sku ||
      facts.format ||
      facts.servingSize ||
      facts.servingsPerContainer ||
      facts.suggestedUse ||
      facts.manufacturedIn ||
      facts.manufacturedFor ||
      facts.certifications?.length ||
      facts.ingredients?.length ||
      facts.contraindications?.length,
  );
}

/**
 * "DATOS DEL PRODUCTO": lo que dice la etiqueta, tal cual. Producción
 * guardaba esto en la base y NO lo enseñaba; aquí es un capítulo más —
 * la lista de ingredientes completa, la porción, el uso sugerido, quién
 * lo fabrica y dónde, sus certificaciones y las contraindicaciones. Es
 * el capítulo que responde "¿qué trae exactamente y quién lo hace?".
 */
export function ProductFactsSheet({ facts, number }: { facts: ProductFactsData; number?: string }) {
  const { t } = useTranslation();

  const specs: { icon: typeof Tag; label: string; value: string }[] = [
    ...(facts.servingSize ? [{ icon: Pill, label: t("Porción"), value: facts.servingSize }] : []),
    ...(facts.servingsPerContainer
      ? [{ icon: Pill, label: t("Porciones por envase"), value: String(facts.servingsPerContainer) }]
      : []),
    ...(facts.format ? [{ icon: Tag, label: t("Formato"), value: facts.format }] : []),
    ...(facts.sku ? [{ icon: Tag, label: t("SKU"), value: facts.sku }] : []),
    ...(facts.manufacturedIn ? [{ icon: Factory, label: t("Fabricado en"), value: facts.manufacturedIn }] : []),
  ];

  return (
    <section className="grid gap-8">
      <ProductBlockHeading eyebrow={t("Datos del producto")} number={number} title={t("Lo que dice la etiqueta")} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        {/* ── Ingredientes y uso ──────────────────────────────────────── */}
        <div className="grid content-start gap-4">
          {facts.ingredients && facts.ingredients.length > 0 && (
            <div className="rounded-[28px] border border-ds-border bg-white p-6 shadow-ds-xs">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
                <FlaskConical className="h-4 w-4 text-ds-violet-pop" aria-hidden="true" />
                {t("Ingredientes")}
              </h3>
              <ul className="mt-3 grid gap-2">
                {facts.ingredients.map((ingredient) => (
                  <li
                    key={ingredient}
                    className="rounded-2xl bg-ds-cream-2/60 px-4 py-2.5 text-[13.5px] leading-[1.55] text-ds-ink-2"
                  >
                    {ingredient}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {facts.suggestedUse && (
            <div className="rounded-[28px] border border-ds-border bg-white p-6 shadow-ds-xs">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
                <Pill className="h-4 w-4 text-ds-violet-pop" aria-hidden="true" />
                {t("Uso sugerido")}
              </h3>
              <p className="mt-2 text-[14px] leading-[1.6] text-ds-ink-2">{facts.suggestedUse}</p>
            </div>
          )}
        </div>

        {/* ── Ficha corta, fabricante, certificaciones, contraindicaciones ── */}
        <div className="grid content-start gap-4">
          {specs.length > 0 && (
            <dl className="grid divide-y divide-ds-border rounded-[28px] border border-ds-border bg-white shadow-ds-xs">
              {specs.map((spec) => (
                <div key={spec.label} className="flex items-start gap-3 px-5 py-3.5">
                  <spec.icon className="mt-0.5 h-4 w-4 shrink-0 text-ds-ink-4" aria-hidden="true" />
                  <dt className="w-32 shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-ds-ink-3">
                    {spec.label}
                  </dt>
                  <dd className="min-w-0 flex-1 break-words text-[13.5px] font-semibold text-ds-ink">
                    {spec.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {facts.manufacturedFor && (
            <p className="rounded-2xl bg-ds-cream-2/60 px-5 py-3.5 text-[12.5px] leading-[1.55] text-ds-ink-3">
              <span className="font-semibold text-ds-ink-2">{t("Fabricado para")}:</span> {facts.manufacturedFor}
            </p>
          )}
          {facts.certifications && facts.certifications.length > 0 && (
            <ul className="flex flex-wrap gap-2" aria-label={t("Certificaciones")}>
              {facts.certifications.map((cert) => (
                <li
                  key={cert}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ds-border bg-white px-3 py-1.5 text-[12px] font-semibold text-ds-ink"
                >
                  <Award className="h-3.5 w-3.5 text-ds-violet-pop" aria-hidden="true" />
                  {cert}
                </li>
              ))}
            </ul>
          )}
          {facts.contraindications && facts.contraindications.length > 0 && (
            <div className="rounded-[28px] border border-ds-amber-soft bg-ds-warn-bg p-5">
              <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
                <AlertTriangle className="h-4 w-4 text-ds-terracotta" aria-hidden="true" />
                {t("Contraindicaciones y avisos")}
              </h3>
              <ul className="mt-2 grid gap-1.5 text-[13px] leading-[1.55] text-ds-ink-2">
                {facts.contraindications.map((item) => (
                  <li key={item} className="pl-4 [text-indent:-1rem]">
                    · {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
